/**
 * The marketing overview's one endpoint.
 *
 * Mirrors `apps/api/src/modules/crm-marketing/overview.routes.ts`. Everything
 * on the page comes from a single answer on purpose: the tiles, the chart and
 * the health card describe the same instant, and three requests would let them
 * describe three - which on a page whose whole job is a set of numbers is a set
 * of numbers that quietly disagree.
 */
import { api } from "./api";
import type { BreakerAck, BreakerRates, BreakerTrip } from "./marketing";

/** Counts keyed by a status name. Zeroes are omitted, so read through a
 *  fallback rather than indexing straight into it. */
export type StatusTotals = Record<string, number>;

export interface DailyPoint {
  /** `YYYY-MM-DD` in the sender's timezone. */
  day: string;
  sent: number;
  /** A COHORT: of the letters that left that day, how many have since come
   *  back this way. Not "how many bounces arrived that day". */
  bounced: number;
  replied: number;
  unsubscribed: number;
  complained: number;
}

export interface OverviewRun {
  id: string;
  campaignId: string | null;
  /** Null when the campaign was deleted - the run survives it. */
  campaignName: string | null;
  trigger: string;
  startedAt: string;
  finishedAt: string | null;
  picked: number;
  sent: number;
  skipped: number;
  failed: number;
  error: string | null;
}

/** The sending domain's reputation, over the breaker's own window. */
export interface DomainHealth {
  windowDays: number;
  rates: BreakerRates;
  /** Null when nothing is over a limit. */
  trip: BreakerTrip | null;
  /** A live acknowledgement is holding a trip back. NOT the same as clean. */
  waived: boolean;
  limits: { bounce: number; complaint: number };
  ack: BreakerAck | null;
}

/**
 * The sender's preflight, as the screen sees it.
 *
 * The same five questions `lib/send/run.ts` asks before a batch, in the same
 * order. `true` means the gate is open, EXCEPT for `stopped` and `testMode`,
 * which are switches that block when on.
 */
export interface Readiness {
  stopped: boolean;
  stopReason: string;
  mailableBase: boolean;
  testMode: boolean;
  channelConfigured: boolean;
  autopilotEnabled: boolean;
}

/**
 * One NACE section, as our register mirror sees it and as Statistics Estonia
 * measured it.
 *
 * The two percentages answer DIFFERENT QUESTIONS and the screen has to keep
 * them apart. `mirrorPct` is how many companies TOLD THE REGISTER a website
 * address, a free-text field nobody is obliged to fill in. `statPct` is how
 * many enterprises told a statistician they have one. The distance between
 * them is what the register cannot see, not a business opportunity.
 */
export interface SectorRow {
  /** NACE Rev 2 section letter, `A` to `U`. */
  section: string;
  /** Active companies the mirror holds in this section. */
  total: number;
  withWeb: number;
  /** `withWeb / total`, one decimal. */
  mirrorPct: number;
  /** Null for `O`, `T` and `U`: the survey does not cover them. */
  statPct: number | null;
  statOrderPct: number | null;
}

export interface SectorBenchmarkMeta {
  source: string;
  /** The attribution line the card must print. The data is CC BY-SA 4.0. */
  attribution: string;
  url: string;
  /** Observation period of the survey, as its own table labels it. */
  year: string;
  /** When the snapshot was taken from the statistics database. */
  retrieved: string;
  /** The smallest enterprise the survey asks. Everything compared against
   *  these figures has to carry this number with it. */
  minEmployees: number;
  economyPct: number | null;
  sizes: { band: string; webPct: number | null }[];
}

export interface SectorView {
  rows: SectorRow[];
  /** Active companies with no usable activity code, so no section. */
  unclassified: number;
  total: number;
  withWeb: number;
  /** ISO instant. The answer may be up to ten minutes old on the server. */
  computedAt: string;
  benchmark: SectorBenchmarkMeta;
}

export interface MarketingOverview {
  /** The zone the days were bucketed in. */
  timezone: string;
  /** The send ledger, grouped by status. The truth. */
  ledger: StatusTotals;
  /** The campaign list's cached counters, summed. Here ONLY so the page can
   *  notice the two disagreeing - nothing is drawn from it. */
  cached: StatusTotals;
  campaigns: StatusTotals;
  suppression: { total: number; bySource: StatusTotals };
  health: DomainHealth;
  daily: DailyPoint[];
  dailyWindowDays: number;
  runs: OverviewRun[];
  /** The register comparison. Null when the mirror could not be counted -
   *  an empty mirror costs the page this card, not its send numbers. */
  sectors: SectorView | null;
  readiness: Readiness;
}

export const getMarketingOverview = (): Promise<MarketingOverview> =>
  api.get("/admin/crm/marketing/overview");
